import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { cleanupAuthState, logAuthEvent, forcePageRefresh, isDesktop } from "@/utils/authUtils";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  refreshAdminStatus: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<{
    error: any | null;
    success?: boolean;
    needsConfirmation?: boolean;
  }>;
  signIn: (email: string, password: string) => Promise<{
    error: any | null;
  }>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{
    error: any | null;
    success?: boolean;
  }>;
  resetPassword: (password: string) => Promise<{
    error: any | null;
    success?: boolean;
  }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    logAuthEvent("AuthProvider initializing", { platform: isDesktop() ? 'desktop' : 'mobile' });
    
    // Set up auth state listener FIRST to avoid missing events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logAuthEvent(`Auth state change: ${event}`, { 
          hasSession: !!session, 
          hasUser: !!session?.user,
          platform: isDesktop() ? 'desktop' : 'mobile'
        });
        
        // Only synchronous state updates here
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        
        // Defer Supabase calls to prevent deadlocks
        if (session?.user) {
          setTimeout(() => {
            checkUserRole(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      logAuthEvent("Initial session check", { 
        hasSession: !!session, 
        hasUser: !!session?.user 
      });
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          checkUserRole(session.user.id);
        }, 0);
      } else {
        setIsAdmin(false);
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkUserRole = async (userId: string | undefined) => {
    if (!userId) {
      setIsAdmin(false);
      return;
    }

    try {
      // Always use the RPC function to check admin role to avoid RLS recursion
      const { data, error } = await supabase.rpc('is_admin_role', {
        user_id: userId
      });
      
      if (error) {
        console.error("Error checking user role:", error);
        setIsAdmin(false);
        return;
      }
      
      setIsAdmin(!!data);
    } catch (error) {
      console.error("Error checking user role:", error);
      setIsAdmin(false);
    }
  };

  // Function to force a refresh of the admin status
  const refreshAdminStatus = async () => {
    if (!user) return;
    
    try {
      await checkUserRole(user.id);
      toast.success("Admin status refreshed");
    } catch (error: any) {
      toast.error("Failed to refresh admin status");
      console.error("Error refreshing admin status:", error);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth?confirmed=true`
        }
      });
      
      if (error) {
        toast.error(error.message);
        return { error };
      }
      
      // Check if email confirmation is required
      const needsConfirmation = !data.session && data.user && !data.user.email_confirmed_at;
      
      if (needsConfirmation) {
        toast.success("Signup successful! Please check your email to confirm your account.");
        return { error: null, success: true, needsConfirmation: true };
      } else {
        toast.success("Signup successful! You can now sign in.");
        return { error: null, success: true, needsConfirmation: false };
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred during signup");
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        toast.error(error.message);
        return { error };
      }
      
      toast.success("Successfully signed in to CraftyJoy!");
      return { error: null };
    } catch (error: any) {
      toast.error(error.message || "An error occurred during sign in");
      return { error };
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) {
        toast.error(error.message);
        return { error };
      }
      
      toast.success("Password reset link sent to your email!");
      return { error: null, success: true };
    } catch (error: any) {
      toast.error(error.message || "An error occurred during password reset request");
      return { error };
    }
  };

  const resetPassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        toast.error(error.message);
        return { error };
      }
      
      toast.success("Password updated successfully!");
      return { error: null, success: true };
    } catch (error: any) {
      toast.error(error.message || "An error occurred during password reset");
      return { error };
    }
  };

  const signOut = async () => {
    try {
      logAuthEvent("Starting logout process");
      
      // Clean up auth state first
      cleanupAuthState();
      
      // Clear local state immediately to prevent UI lag
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      
      // Attempt global sign out (fallback if it fails)
      try {
        await supabase.auth.signOut({ scope: 'global' });
        logAuthEvent("Supabase signOut successful");
      } catch (signOutError) {
        logAuthEvent("Supabase signOut failed, continuing with cleanup", signOutError);
        // Continue with the process even if signOut fails
      }
      
      toast.success("Successfully signed out from CraftyJoy");
      
      // Force page refresh for desktop to ensure clean state
      if (isDesktop()) {
        logAuthEvent("Desktop detected, forcing page refresh");
        setTimeout(() => {
          forcePageRefresh('/auth');
        }, 500); // Small delay to show toast
      }
      
    } catch (error: any) {
      logAuthEvent("Logout error", error);
      toast.error(error.message || "An error occurred during sign out");
      
      // Even on error, try to clean up and redirect
      cleanupAuthState();
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      
      if (isDesktop()) {
        setTimeout(() => {
          forcePageRefresh('/auth');
        }, 1000);
      }
    }
  };

  const value = {
    session,
    user,
    isAdmin,
    isLoading,
    refreshAdminStatus,
    signUp,
    signIn,
    signOut,
    requestPasswordReset,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
